// Site-wide constants for OperaSafe.cl
export const BRAND = "OperaSafe.cl";
export const SLOGAN = "Protegemos todo lo que mueve tu operación industrial";
export const CONTACT_EMAIL = "Contacto@OperaSafe.cl";
// Teléfono comercial (mismo número del WhatsApp): uno para mostrar y otro para el enlace tel:
export const CONTACT_PHONE = "+56 9 4296 4199";
export const CONTACT_PHONE_TEL = "+56942964199";

// Contacto comercial: ejecutivo que ayuda a cotizar y conseguir la mejor opción.
export const COMMERCIAL_WHATSAPP_DIGITS = "56942964199";
export const COMMERCIAL_WHATSAPP_MESSAGE =
  "Hola, quiero cotizar un seguro para mi operación industrial: maquinaria, equipos, planta, flota o colaboradores.";

// Contacto de gestión de siniestros.
export const CLAIMS_WHATSAPP_DIGITS = "56942964199";
export const CLAIMS_WHATSAPP_MESSAGE =
  "Hola, necesito asistencia con un siniestro de mi operación: maquinaria, equipos, planta, vehículo, carga o un colaborador.";

export const whatsappUrl = () =>
  `https://wa.me/${COMMERCIAL_WHATSAPP_DIGITS}?text=${encodeURIComponent(COMMERCIAL_WHATSAPP_MESSAGE)}`;

export const claimsWhatsappUrl = () =>
  `https://wa.me/${CLAIMS_WHATSAPP_DIGITS}?text=${encodeURIComponent(CLAIMS_WHATSAPP_MESSAGE)}`;

// Interruptor de correos: mientras esté en false el panel no muestra los botones
// de envío por email (la gestión es manual desde el panel).
export const EMAILS_ENABLED = false;

// Aseguradoras de respaldo. `h` = alto en px para equilibrar ópticamente los logos.
export const INSURERS = [
  { id: "mapfre", name: "MAPFRE", logo: "/brand/insurers/mapfre.png", h: 22 },
  { id: "bci", name: "BCI Seguros", logo: "/brand/insurers/bci.png", h: 30 },
  { id: "sura", name: "SURA", logo: "/brand/insurers/sura.png", h: 26 },
  { id: "zurich", name: "Zurich", logo: "/brand/insurers/zurich.png", h: 26 },
  { id: "hdi", name: "HDI Seguros", logo: "/brand/insurers/hdi.png", h: 34 },
  { id: "reale", name: "Reale Seguros", logo: "/brand/insurers/reale.png", h: 30 },
  {
    id: "renta-nacional",
    name: "Renta Nacional",
    logo: "/brand/insurers/renta-nacional.png",
    h: 26,
  },
];

// ---------------------------------------------------------------------------
// Las 9 líneas de seguro de OperaSafe.cl
// El `value` debe coincidir con INDUSTRIA_VALUES del backend.
// ---------------------------------------------------------------------------
export const PRODUCT_OPTIONS = [
  {
    value: "maquinaria",
    label: "Maquinaria",
    short: "Maquinaria",
    desc: "Maquinaria pesada, móvil y de faena.",
  },
  {
    value: "equipos",
    label: "Equipos",
    short: "Equipos",
    desc: "Equipos industriales y electrónicos.",
  },
  {
    value: "plantas",
    label: "Plantas",
    short: "Plantas",
    desc: "Plantas, bodegas e instalaciones.",
  },
  {
    value: "infraestructura",
    label: "Ingeniería",
    short: "Ingeniería",
    desc: "Obras, ampliaciones y montaje.",
  },
  {
    value: "flotas",
    label: "Flotas",
    short: "Flotas",
    desc: "Camionetas, camiones y buses de la empresa.",
  },
  {
    value: "transporte",
    label: "Transporte",
    short: "Transporte",
    desc: "Carga terrestre, marítima y aérea.",
  },
  {
    value: "responsabilidad_civil",
    label: "Responsabilidad Civil",
    short: "Responsabilidad Civil",
    desc: "Reclamos de terceros: general, patronal y productos.",
  },
  {
    value: "colaboradores",
    label: "Colaboradores",
    short: "Colaboradores",
    desc: "Accidentes personales y autos a precio flota.",
  },
  {
    value: "fiel_cumplimiento",
    label: "Garantías",
    short: "Garantías",
    desc: "Fiel cumplimiento, seriedad de la oferta, anticipos y correcta ejecución de las obras.",
  },
];

// Alias legacy — varios componentes ya importan INDUSTRIA_OPTIONS.
export const INDUSTRIA_OPTIONS = PRODUCT_OPTIONS;

export const PRODUCT_LABELS = PRODUCT_OPTIONS.reduce(
  (acc, p) => ({ ...acc, [p.value]: p.label }),
  {}
);

// ---------------------------------------------------------------------------
// Sub-coberturas de la línea "Colaboradores".
// Los `id` deben coincidir con SUBCOBERTURA_LABELS del backend.
// ---------------------------------------------------------------------------
export const SUB_COBERTURAS = [
  {
    id: "accidentes_personales",
    name: "Accidentes personales colectivo",
    short: "Accidentes personales",
    desc: "Muerte accidental, invalidez y gastos médicos para tus trabajadores.",
  },
  {
    id: "autos_precio_flota",
    name: "Autos a precio flota para colaboradores (hasta 60% de ahorro)",
    short: "Autos a precio flota",
    desc: "El seguro del auto particular de cada colaborador a tarifa de flota.",
  },
];

export const SUB_COBERTURA_LABELS = SUB_COBERTURAS.reduce(
  (acc, s) => ({ ...acc, [s.id]: s.name }),
  {}
);

export const SUB_COBERTURA_SHORT = SUB_COBERTURAS.reduce(
  (acc, s) => ({ ...acc, [s.id]: s.short }),
  {}
);

// ---------------------------------------------------------------------------
// Las 10 coberturas técnicas que estructuran las pólizas industriales.
// Se muestran en la sección "Coberturas" del sitio.
// ---------------------------------------------------------------------------
export const COVERAGE_LINES = [
  {
    id: "todo_riesgo_industrial",
    producto: "plantas",
    image: "/generated/op-cob-todo-riesgo-industrial.jpg",
    name: "Todo Riesgo Industrial",
    tag: "Plantas · Bodegas · Instalaciones",
    desc:
      "Protege el edificio, la maquinaria fija, las existencias y todo el patrimonio de tu planta frente a los daños materiales que pueden detener la operación.",
    coberturas: [
      "Incendio, rayo y explosión",
      "Sismo y salida de mar",
      "Daño por agua y rotura de matrices",
      "Viento, lluvia e inundación",
      "Robo con fuerza y asalto",
      "Daño malicioso, huelga y desorden popular",
      "Remoción de escombros",
      "Honorarios profesionales y gastos de reconstrucción",
    ],
    icon: "factory",
  },
  {
    id: "averia_maquinaria",
    producto: "maquinaria",
    image: "/generated/op-cob-averia-maquinaria.jpg",
    name: "Avería de Maquinaria",
    tag: "Rotura de maquinaria · Fallas eléctricas",
    desc:
      "Cubre la rotura súbita e imprevista de tus máquinas, incluso cuando el daño se produce por un error del propio operador.",
    coberturas: [
      "Rotura por fuerza centrífuga",
      "Falla eléctrica, cortocircuito y sobretensión",
      "Error de operación del personal",
      "Defectos de fabricación, material o montaje",
      "Falta de agua en calderas",
      "Daños en montaje y desmontaje por mantención",
      "Repuestos y fletes de urgencia",
    ],
    icon: "wrench",
  },
  {
    id: "paralizacion",
    producto: "plantas",
    image: "/generated/op-cob-paralizacion.jpg",
    name: "Perjuicio por Paralización",
    tag: "Lucro cesante · Continuidad operacional",
    desc:
      "Cuando la planta se detiene, los costos siguen corriendo. Esta cobertura repone la utilidad y los gastos fijos mientras vuelves a producir.",
    coberturas: [
      "Pérdida de utilidad bruta",
      "Gastos fijos y remuneraciones",
      "Gastos extraordinarios para reanudar la operación",
      "Paralización por incendio o daño material",
      "Paralización por avería de maquinaria",
      "Interrupción por proveedores o clientes clave",
    ],
    icon: "pause",
  },
  {
    id: "equipos_moviles",
    producto: "maquinaria",
    image: "/generated/op-cob-equipos-moviles.jpg",
    name: "Equipos Móviles y Maquinaria Pesada",
    tag: "Grúas · Cargadores · Grúas horquilla",
    desc:
      "Todo riesgo de equipo y maquinaria de contratistas (TREC): cubre tus equipos dentro y fuera de la faena, incluso en traslado.",
    coberturas: [
      "Daños físicos accidentales",
      "Volcamiento y colisión",
      "Robo del equipo y de sus partes",
      "Incendio y explosión",
      "Cobertura dentro y fuera de faena",
      "Traslado entre faenas",
      "Responsabilidad Civil de operación (opcional)",
    ],
    icon: "forklift",
  },
  {
    id: "equipos_electronicos",
    producto: "equipos",
    image: "/generated/op-cob-equipos-electronicos.jpg",
    name: "Equipos Electrónicos",
    tag: "Control · Servidores · Instrumentación",
    desc:
      "Para todo lo que hace funcionar la operación en silencio: tableros, PLC, servidores, balanzas, instrumentos y equipos portátiles.",
    coberturas: [
      "Daño accidental a equipos fijos y portátiles",
      "Cortocircuito y sobretensión",
      "Daño por agua o humedad",
      "Robo con fuerza",
      "Portadores externos de datos",
      "Gastos adicionales de operación",
      "Equipos en tránsito",
    ],
    icon: "cpu",
  },
  {
    id: "construccion_montaje",
    producto: "infraestructura",
    image: "/generated/op-cob-construccion-montaje.jpg",
    name: "Todo Riesgo Construcción y Montaje",
    tag: "Obras · Ampliaciones · Montaje",
    desc:
      "Pólizas CAR y EAR para tus proyectos: cubre la obra en ejecución, los equipos de construcción y la responsabilidad frente a terceros.",
    coberturas: [
      "Daños a la obra en ejecución",
      "Maquinaria y equipos de construcción",
      "Pruebas y puesta en marcha",
      "Responsabilidad Civil frente a terceros",
      "Período de mantención",
      "Remoción de escombros",
      "Daños por eventos de la naturaleza",
    ],
    icon: "construction",
  },
  {
    id: "flotas",
    producto: "flotas",
    image: "/generated/op-cob-flotas.jpg",
    name: "Flotas y Vehículos Comerciales",
    tag: "Camionetas · Camiones · Buses",
    desc:
      "Toda la flota de la empresa bajo una sola póliza, con un vencimiento único, administración centralizada y tarifas por volumen.",
    coberturas: [
      "Daño propio y pérdida total",
      "Robo y hurto",
      "Responsabilidad Civil por daños a terceros",
      "Asistencia en ruta 24/7",
      "Deducible negociado por volumen",
      "Vehículo de reemplazo (opcional)",
      "Altas y bajas de vehículos en línea",
    ],
    icon: "truck",
  },
  {
    id: "transporte_carga",
    producto: "transporte",
    image: "/generated/op-cob-transporte-carga.jpg",
    name: "Transporte de Carga",
    tag: "Terrestre · Marítimo · Aéreo",
    desc:
      "Protege la mercadería mientras se mueve: insumos, productos terminados, importaciones y exportaciones, puerta a puerta.",
    coberturas: [
      "Póliza por viaje o flotante anual",
      "Importaciones y exportaciones",
      "Carga propia y de terceros",
      "Robo y asalto en ruta",
      "Volcamiento, colisión y caída de la carga",
      "Mojadura y contaminación",
      "Avería gruesa y gastos de salvamento",
    ],
    icon: "ship",
  },
  {
    id: "responsabilidad_civil",
    producto: "responsabilidad_civil",
    image: "/generated/op-cob-responsabilidad-civil.jpg",
    name: "Responsabilidad Civil",
    tag: "General · Patronal · Productos",
    desc:
      "Responde por los daños que tu operación pueda causar a terceros y protege el patrimonio de la empresa ante demandas.",
    coberturas: [
      "RC general de la empresa",
      "RC patronal (empleador)",
      "RC de productos",
      "RC cruzada entre contratistas",
      "RC por vehículos y equipos en faena",
      "Gastos de defensa judicial",
      "Daño emergente a terceros",
    ],
    icon: "scale",
  },
  {
    id: "garantias",
    producto: "fiel_cumplimiento",
    image: "/generated/op-cob-garantias.jpg",
    name: "Garantías",
    tag: "Contratos · Licitaciones · Anticipos",
    desc:
      "Fiel cumplimiento, seriedad de la oferta, anticipos y correcta ejecución de las obras: garantías que reemplazan a la boleta bancaria sin inmovilizar tu línea de crédito.",
    coberturas: [
      "Fiel cumplimiento de contrato",
      "Seriedad de la oferta",
      "Correcta ejecución y buena calidad",
      "Devolución de anticipos",
      "Obligaciones laborales y previsionales",
      "Contratos públicos y privados",
      "Emisión rápida, sin inmovilizar tu línea bancaria",
    ],
    icon: "fileCheck",
  },
];

export const COVERAGE_LINE_LABELS = COVERAGE_LINES.reduce(
  (acc, c) => ({ ...acc, [c.id]: c.name }),
  {}
);
